import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import 'url-search-params-polyfill'
import {
  Header,
  Container,
  Grid
} from 'semantic-ui-react'
import { isNil } from 'ramda'
// Local
import {
  API_URL,
  AUTH_STATUS,
  AUTH_TYPE
} from '../../constant'
import { api } from '../../action/api'
import {
  login,
  logout,
  mfaLogin,
  mfaTotpLogin,
  mfaQrTotpLogin,
  withCurrentUser
} from '../../action/user'
import LoginEmpty from './loginEmpty'
import LoginForm from './LoginForm'
import MfaForm from './mfaForm'
import TotpMfaForm from './totpMfaForm'
import QrTotpMfaFormComponent from './qrTotpMfaForm'
import { AutoLoginParams } from '../../../types'
import { User } from '../authenticated/types'
import { LoginLogo } from '../common/brandingUtils'
// Styling
import './index.css'

const queryParamsToObject = (
  queryParams: string
): AutoLoginParams => {
  const params = new URLSearchParams(queryParams)
  const result: Record<string, string> = {}
  params.forEach((value, key) => {
    result[key] = value
  })
  return result as AutoLoginParams
}

type Props = {
  email: string
  username: string
  password: string
  authMessage: string
  authStatus: string
  authAttempts: number
  authType: string
  qrCodeUuid: string
  dispatch: any
}

function Login(
  props: Props
): JSX.Element {
  const {
    email,
    username,
    password,
    authMessage,
    authStatus,
    authAttempts,
    authType,
    qrCodeUuid,
    dispatch
  } = props

  const queryParams = queryParamsToObject(window.location.search)

  const [
    isLoading,
    setIsLoading
  ] = useState<boolean>(true)

  const handleLoginSubmit = (
    usernameProp: string,
    passwordProp: string,
    recaptchaToken: string | null
  ) => {
    logout('', dispatch, () => {
      login(
        usernameProp,
        passwordProp,
        recaptchaToken,
        authAttempts,
        queryParams,
        dispatch
      )
    })
  }

  const handleMfaSubmit = (
    mfaCodeProp: string,
    emailProp: string,
    usernameProp: string,
    passwordProp: string,
    rememberMeProp: string
  ) => {
    mfaLogin(
      mfaCodeProp,
      emailProp,
      usernameProp,
      passwordProp,
      rememberMeProp === 'true',
      authAttempts,
      queryParams,
      dispatch
    )
  }

  const handleMfaTotpSubmit = (
    mfaCodeProp: string,
    emailProp: string,
    usernameProp: string,
    passwordProp: string,
    rememberMeProp: string,
    authTypeProp: string,
    qrCodeUuidProp: string
  ) => {
    mfaTotpLogin(
      mfaCodeProp,
      emailProp,
      usernameProp,
      passwordProp,
      rememberMeProp === 'true',
      authAttempts,
      authTypeProp,
      qrCodeUuidProp,
      queryParams,
      dispatch
    )
  }

  const handleMfaQrTotpSubmit = (
    mfaCodeProp: string,
    emailProp: string,
    usernameProp: string,
    passwordProp: string,
    rememberMeProp: string,
    authTypeProp: string,
    qrCodeUuidProp: string
  ) => {
    mfaQrTotpLogin(
      mfaCodeProp,
      emailProp,
      usernameProp,
      passwordProp,
      rememberMeProp === 'true',
      authAttempts,
      authTypeProp,
      qrCodeUuidProp,
      queryParams,
      dispatch
    )
  }


  useEffect(() => {
    let isMounted = true
    const fetchCurrentUser = () => {
      const url = API_URL.USERS_CURRENT
      api
        .getUnsafe(url)
        .then(({ data }: { data: User }) => {
          if (!isMounted) return
          withCurrentUser(
            data,
            queryParams.encounter,
            dispatch
          )
        })
        .catch(() => {
          if (!isMounted) return
          setIsLoading(false)
        })
    }
    fetchCurrentUser()
    return () => {
      isMounted = false
    }
  }, [])

  const renderEmpty = () => (
    <Container>
      <div className="ui stackable grid">
        <Grid.Row
          id="c-login-row-computer"
          columns={ 3 }
          only="computer"
        >
          <Grid.Column />
          <Grid.Column>
            <LoginEmpty
              errorMessage={ authMessage }
              infoMessage={ null }
            />
          </Grid.Column>
          <Grid.Column />
        </Grid.Row>
        <Grid.Row columns={ 1 } only="mobile tablet" id="c-login-row-mobile">
          <Grid.Column>
            <LoginEmpty
              errorMessage={ authMessage }
              infoMessage={ null }
            />
          </Grid.Column>
        </Grid.Row>
      </div>
    </Container>
  )

  const renderMfaForm = () => {
    const key = `mfa-form-key-${(new Date()).getTime()}`
    return (
      <Container>
        <div className="ui stackable grid">
          <Grid.Row />
          <Grid.Row columns={ 3 } id="c-mfa-header-row-computer">
            <Grid.Column />
            <Grid.Column>
              <h2 className="ui header">
                <Header.Content>
                  { LoginLogo() }
                </Header.Content>
              </h2>
            </Grid.Column>
            <Grid.Column />
          </Grid.Row>
        </div>
        <div className="ui stackable grid">
          <Grid.Row columns={ 1 } only="computer" id="c-mfa-login-row-computer">
            <Grid.Column />
            <Grid.Column>
              <MfaForm
                key={ `${key}-computer` }
                id="computer"
                onSubmit={ handleMfaSubmit }
                email={ email || '' }
                username={ username || '' }
                password={ password || '' }
                errorMessage={ authMessage }
              />
            </Grid.Column>
            <Grid.Column />
          </Grid.Row>
          <Grid.Row columns={ 1 } only="mobile tablet" id="c-mfa-login-row-mobile">
            <Grid.Column />
            <Grid.Column>
              <MfaForm
                key={ `${key}-mobile` }
                id="mobile"
                onSubmit={ handleMfaSubmit }
                email={ email || '' }
                username={ username || '' }
                password={ password || '' }
                errorMessage={ authMessage }
              />
            </Grid.Column>
            <Grid.Column />
          </Grid.Row>
        </div>
      </Container>
    )
  }

  const renderTotpMfaForm = () => {
    const key = `mfa-form-key-${(new Date()).getTime()}`
    return (
      <Container>
        <div className="ui stackable grid">
          <Grid.Row />
          <Grid.Row columns={ 3 } id="c-mfa-header-row-computer">
            <Grid.Column />
            <Grid.Column>
              <h2 className="ui header">
                <Header.Content>
                  { LoginLogo() }
                </Header.Content>
              </h2>
            </Grid.Column>
            <Grid.Column />
          </Grid.Row>
        </div>
        <div className="ui stackable grid">
          <Grid.Row columns={ 1 } only="computer" id="c-mfa-login-row-computer">
            <Grid.Column />
            <Grid.Column>
              <TotpMfaForm
                key={ `${key}-computer` }
                id="computer"
                onSubmit={ handleMfaTotpSubmit }
                email={ email || '' }
                username={ username || '' }
                password={ password || '' }
                errorMessage={ authMessage }
                qrCodeUuid=""
              />
            </Grid.Column>
            <Grid.Column />
          </Grid.Row>
          <Grid.Row columns={ 1 } only="mobile tablet" id="c-mfa-login-row-mobile">
            <Grid.Column />
            <Grid.Column>
              <TotpMfaForm
                key={ `${key}-mobile` }
                id="mobile"
                onSubmit={ handleMfaTotpSubmit }
                email={ email || '' }
                username={ username || '' }
                password={ password || '' }
                errorMessage={ authMessage }
                qrCodeUuid=""
              />
            </Grid.Column>
            <Grid.Column />
          </Grid.Row>
        </div>
      </Container>
    )
  }

  const renderQrTotpMfaFormComponent = () => {
    const key = `mfa-form-key-${(new Date()).getTime()}`
    return (
      <Container>
        <div className="ui stackable grid">
          <Grid.Row />
          <Grid.Row columns={ 3 } id="c-mfa-header-row-computer">
            <Grid.Column />
            <Grid.Column>
              <h2 className="ui header">
                <Header.Content>
                  { LoginLogo() }
                </Header.Content>
              </h2>
            </Grid.Column>
            <Grid.Column />
          </Grid.Row>
        </div>
        <div className="ui stackable grid">
          <Grid.Row columns={ 1 } only="computer" id="c-mfa-login-row-computer">
            <Grid.Column />
            <Grid.Column>
              <QrTotpMfaFormComponent
                key={ `${key}-computer` }
                id="computer"
                onSubmit={ handleMfaQrTotpSubmit }
                email={ email || '' }
                username={ username || '' }
                password={ password || '' }
                errorMessage={ authMessage }
                qrCodeUuid={ qrCodeUuid }
              />
            </Grid.Column>
            <Grid.Column />
          </Grid.Row>
          <Grid.Row columns={ 1 } only="mobile tablet" id="c-mfa-login-row-mobile">
            <Grid.Column />
            <Grid.Column>
              <QrTotpMfaFormComponent
                key={ `${key}-mobile` }
                id="mobile"
                onSubmit={ handleMfaQrTotpSubmit }
                email={ email || '' }
                username={ username || '' }
                password={ password || '' }
                errorMessage={ authMessage }
                qrCodeUuid={ qrCodeUuid }
              />
            </Grid.Column>
            <Grid.Column />
          </Grid.Row>
        </div>
      </Container>
    )
  }

  const renderLoginForm = () => (
    <Container>
      <div className="ui stackable grid">
        <Grid.Row columns={ 3 } only="computer" id="c-login-row-computer">
          <Grid.Column />
          <Grid.Column>
            <LoginForm
              id="computer"
              onSubmit={ handleLoginSubmit }
              username={ username || '' }
              errorMessage={ authMessage }
            />
          </Grid.Column>
          <Grid.Column />
        </Grid.Row>
        <Grid.Row columns={ 1 } only="mobile tablet" id="c-login-row-mobile">
          <Grid.Column>
            <LoginForm
              id="mobile"
              onSubmit={ handleLoginSubmit }
              username={ username }
              errorMessage={ authMessage }
            />
          </Grid.Column>
        </Grid.Row>
      </div>
    </Container>
  )

  const renderMfa = () => !(!isNil(authType) && authType.includes(AUTH_TYPE.TOTP)) ? renderMfaForm() :
    authType === AUTH_TYPE.QR_TOTP ? renderQrTotpMfaFormComponent() : renderTotpMfaForm()

  return isLoading ? renderEmpty() :
    authStatus === AUTH_STATUS.MFA_REQUIRED ? renderMfa() : renderLoginForm()
}

export default connect(
  (state: any) => ({
    email: state.user.email,
    username: state.user.username,
    password: state.user.password,
    authMessage: state.user.authMessage,
    authStatus: state.user.authStatus,
    authAttempts: state.user.authAttempts,
    authType: state.user.authType,
    qrCodeUuid: state.user.qrCodeUuid
  })
)(Login)
